import { UserContext, type UserContextProps, useUser } from '@faceup/member'
import { useFeatureFlag } from '@faceup/posthog'
import {
  ChargebeeSubscriptionStatus,
  Feature,
  FrontendPermissionType,
  Institution,
} from '@faceup/utils'
import { useContext } from 'react'
import { useInstitution } from '../providers'
import { useEntitlement } from './useEntitlement'
import { usePartner } from './usePartner'

export type UseAccessRightsReturn = {
  isAllowedAdvancedCustomization: boolean
  isAllowedSettingsAccess: boolean
  isAllowedSSO: boolean
  isAllowedWebhooks: boolean
  isAllowedApi: boolean
  isVisibleApi: boolean
  isVisibleWhitelabel: boolean
  isAllowedWhitelabel: boolean
  isVisibleEnableAccessKey: boolean
  isAllowedEnableAccessKey: boolean
  isAllowedE2EE: boolean
  areReportsNeedingAttentionVisible: boolean
  areVisibleWebhooks: boolean
  isBioVisible: boolean
  isAllowedMultipleReportSources: boolean
  isVisiblePdfPasswordAlert: boolean
  isAllowedRedaction: boolean
  isAllowedStatistics: boolean
  isAllowedSurveys: boolean
  isAllowedInvestigations: boolean
  isAllowedNewInvestigations: boolean
  isAllowedDisclosures: boolean
  isAllowedCustomizationEdit: boolean
  isVisibleForPermission: Record<FrontendPermissionType, boolean>
  isPartnerAdministration: boolean
  permissionsWithInstitutionIds: Record<FrontendPermissionType, string[]>
  isAllowedIntegration: boolean
  isAllowedReportsBadge: boolean
  isAllowedCreateAutomatedHotline: boolean
  isAllowedCreateLiveHotline: boolean
  isAllowedCreateAiHotline: boolean
  areReportAttachmentsVisible: boolean
  areVisibleDataManagementEmployees: boolean
  areVisibleReportEmployees: boolean
  isAllowedIpAllowlist: boolean
  isSettingsVisible: boolean
  isAllowedEmployeeManagement: boolean
}

type FnUseAccessRights = () => UseAccessRightsReturn

export const useAccessRights: FnUseAccessRights = () => {
  const {
    data: { usePaidVersion, institution, subscriptionStatus, hasEmployees },
  } = useInstitution({
    // Should be better separated
    __unsafeSkipCheckIfNotWrappedInInstitutionProvider: true,
  })
  const { hasPermissions, hasAtLeastOnePermission } = usePermissions()
  const { isPartnerAdministration } = usePartner()
  const { isFeatureFlagEnabled } = useFeatureFlag()
  const { viewer } = useContext(UserContext)
  const permissionsWithInstitutionIds = usePermissionsWithInstitutionIds(viewer)
  const isEntitlementEnabled = useEntitlement()

  const isActive = [
    ChargebeeSubscriptionStatus.Active,
    ChargebeeSubscriptionStatus.NonRenewing,
  ].includes(subscriptionStatus ?? ChargebeeSubscriptionStatus.Cancelled)

  /*
   * isAllowed - has access to item (e.g. to persist data) - to show buy higher pricing plan info
   * isVisible - is item visible or fully hidden in page
   */
  const isSchool = institution === Institution.School
  const isFreeOrNotActiveInstitution = !usePaidVersion || !isActive

  const isVisibleForPermission = Object.values(FrontendPermissionType).reduce(
    (acc, permission) => ({
      ...acc,
      [permission]: hasPermissions(permission),
    }),
    {} as Record<FrontendPermissionType, boolean>
  )

  return {
    // Instead of changing all the places where this property is used I assume that partner is always allowed to
    // edit advanced customization for partner config (motherId does not exist). For institution config the same
    // rules as for other users still apply.
    isAllowedAdvancedCustomization: usePaidVersion,
    isAllowedSettingsAccess: isVisibleForPermission.SettingsAccess,
    isAllowedSSO: isEntitlementEnabled(Feature.Sso),
    isAllowedWebhooks: isEntitlementEnabled(Feature.Webhooks),
    isAllowedApi: isEntitlementEnabled(Feature.Api),
    isVisibleApi: isVisibleForPermission.SettingsAccess && !(isSchool && !usePaidVersion),
    isVisibleWhitelabel: !isSchool && usePaidVersion && isVisibleForPermission.SettingsAccess,
    isAllowedWhitelabel: isEntitlementEnabled(Feature.Whitelabel),
    isAllowedInvestigations: isEntitlementEnabled(Feature.Investigations),
    isAllowedNewInvestigations:
      isEntitlementEnabled(Feature.NewInvestigations) ||
      isFeatureFlagEnabled('investigations-early-access'),
    isAllowedDisclosures: isEntitlementEnabled(Feature.Disclosures),
    isVisibleEnableAccessKey: isSchool,
    isAllowedEnableAccessKey: usePaidVersion,
    isAllowedE2EE: usePaidVersion,
    areReportsNeedingAttentionVisible: usePaidVersion && isVisibleForPermission.ReportAccess,
    areVisibleWebhooks: isVisibleForPermission.SettingsAccess && !(isSchool && !usePaidVersion),
    isBioVisible: usePaidVersion,
    isAllowedMultipleReportSources: usePaidVersion && isActive,
    isVisiblePdfPasswordAlert: !usePaidVersion,
    isAllowedRedaction: usePaidVersion,
    isAllowedStatistics: isVisibleForPermission.Analytics,
    isAllowedSurveys: isVisibleForPermission.Surveys && usePaidVersion,
    isAllowedCustomizationEdit: usePaidVersion && isVisibleForPermission.ManageReportingChannels,
    isVisibleForPermission,
    isPartnerAdministration,
    permissionsWithInstitutionIds,
    isAllowedIntegration:
      isEntitlementEnabled(Feature.Integrations) && isVisibleForPermission.SettingsAccess,
    isAllowedReportsBadge: usePaidVersion,
    isAllowedCreateAutomatedHotline: !isFreeOrNotActiveInstitution,
    isAllowedCreateLiveHotline: !isFreeOrNotActiveInstitution,
    isAllowedCreateAiHotline: !isFreeOrNotActiveInstitution,
    areReportAttachmentsVisible: usePaidVersion,
    areVisibleDataManagementEmployees: hasEmployees,
    areVisibleReportEmployees: hasEmployees,
    isAllowedIpAllowlist: isEntitlementEnabled(Feature.IpAllowlist),
    isSettingsVisible: hasAtLeastOnePermission(
      FrontendPermissionType.SettingsAccess,
      FrontendPermissionType.ManageUsers,
      FrontendPermissionType.ManageOrganizationalUnits,
      FrontendPermissionType.BillingAccess
    ),
    isAllowedEmployeeManagement: isEntitlementEnabled(Feature.EmployeeManagement),
  }
}

const usePermissions = () => {
  const {
    data: { permissions },
  } = useInstitution({
    // Should be separated from partner/institution
    // It's not auth related thing
    __unsafeSkipCheckIfNotWrappedInInstitutionProvider: true,
  })
  const user = useUser()

  const enabledPermissionsTypes = permissions
    .filter(permission => permission.enabled)
    .map(permission => permission.type)

  const hasPermissions = (...requiredPermissions: FrontendPermissionType[]) => {
    if (user.application === 'kredenc') {
      return true
    }

    return requiredPermissions.every(requiredPermission =>
      enabledPermissionsTypes.includes(requiredPermission)
    )
  }

  const hasAtLeastOnePermission = (...requiredPermissions: FrontendPermissionType[]) => {
    if (user.application === 'kredenc') {
      return true
    }

    return requiredPermissions.some(requiredPermission =>
      enabledPermissionsTypes.includes(requiredPermission)
    )
  }

  return {
    hasPermissions,
    hasAtLeastOnePermission,
  }
}

const usePermissionsWithInstitutionIds = (viewer: UserContextProps['viewer']) => {
  const organizationalUnitsWithAccess = new Set(viewer?.partnerCompanyIds)
  const partnerInstitutionIds = new Set(viewer?.partnerInstitutionIds)

  return Object.values(FrontendPermissionType).reduce(
    (acc, permission) => ({
      ...acc,
      [permission]:
        viewer?.keys.partnerPermissions
          ?.filter(
            partnerPermission => partnerPermission.type === permission && partnerPermission.enabled
          )
          .map(permission => permission.motherId)
          .filter(id => {
            if (permission === FrontendPermissionType.Analytics) {
              // for analytics it's important only to have InstitutionAccess
              return partnerInstitutionIds.has(id)
            }
            return organizationalUnitsWithAccess.has(id)
          }) ?? [],
    }),
    {} as Record<FrontendPermissionType, string[]>
  )
}
